import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Monitor, MousePointer, Keyboard, WifiOff,
  Minimize2, Maximize2, RefreshCw, Clock, Activity, Zap, Shield,
  Lock, Globe, Cpu, Eye, ArrowRight, CheckCircle, Layers, Wifi,
} from 'lucide-react';

const API_BASE = 'https://gateway.checkscript.online/api/v1/cf-calls';
const RECONNECT_DELAYS = [1500, 3000, 5000, 8000, 12000];
const MAX_RECONNECT_ATTEMPTS = 5;
const INPUT_FLUSH_MS = 16;
const DEBUG_MODE = false;

const _debugLogs = [];
let _debugSubscribers = [];

function debugLog(tag, msg, data) {
  const ts = new Date().toLocaleTimeString('en-GB', { hour12: false });
  const entry = { ts, tag, msg, data: data !== undefined ? JSON.stringify(data).substring(0, 300) : '', id: Date.now() + Math.random() };
  _debugLogs.push(entry);
  if (_debugLogs.length > 80) _debugLogs.shift();
  _debugSubscribers.forEach(fn => fn([..._debugLogs]));
  if (DEBUG_MODE) console.log(`[${tag}] ${msg}`, data !== undefined ? data : '');
}

function useDebugLogs() {
  const [logs, setLogs] = useState([..._debugLogs]);
  useEffect(() => {
    _debugSubscribers.push(setLogs);
    return () => { _debugSubscribers = _debugSubscribers.filter(fn => fn !== setLogs); };
  }, []);
  return logs;
}

async function apiFetch(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const method = options.method || 'GET';
  const { timeoutMs = 10000, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  debugLog('HTTP', `${method} ${path}`, null);
  const t0 = Date.now();
  try {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      ...fetchOptions,
      signal: controller.signal,
    });
    const ms = Date.now() - t0;
    const json = await res.json();
    debugLog('HTTP', `${method} ${path} → ${res.status} (${ms}ms)`, { success: json.success, error: json.error });
    return json;
  } catch (err) {
    const ms = Date.now() - t0;
    const message = err.name === 'AbortError' ? 'Request timeout' : err.message;
    debugLog('HTTP', `${method} ${path} FAILED (${ms}ms): ${message}`, null);
    return { success: false, error: message, timeout: err.name === 'AbortError' };
  } finally {
    clearTimeout(timeout);
  }
}

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function useConnectionQuality(pcRef) {
  const [quality, setQuality] = useState({
    latency: 0, packetLoss: 0, jitter: 0, connectionType: '',
  });

  useEffect(() => {
    const interval = setInterval(async () => {
      const pc = pcRef.current;
      if (!pc || pc.connectionState !== 'connected') return;
      try {
        const stats = await pc.getStats();
        stats.forEach((report) => {
          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            setQuality((prev) => ({
              ...prev,
              latency: Math.round(report.currentRoundTripTime * 1000) || prev.latency,
            }));
          }
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            const loss = report.packetsLost || 0;
            const received = report.packetsReceived || 1;
            setQuality((prev) => ({
              ...prev,
              packetLoss: ((loss / (loss + received)) * 100).toFixed(1),
              jitter: report.jitter ? (report.jitter * 1000).toFixed(0) : prev.jitter,
            }));
          }
          if (report.type === 'local-candidate') {
            setQuality((prev) => ({
              ...prev,
              connectionType: report.candidateType || prev.connectionType,
            }));
          }
        });
      } catch { }
    }, 2000);
    return () => clearInterval(interval);
  }, [pcRef]);

  return quality;
}

const BufferingOverlay = ({ isBuffering }) => {
  if (!isBuffering) return null;
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 z-40 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-4 animate-pulse">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-purple-500 border-b-transparent rounded-full animate-spin"
               style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
        <span className="text-white text-sm font-medium tracking-wide">Buffering...</span>
      </div>
    </div>
  );
};

const ReconnectOverlay = ({ status, attempt, maxAttempts }) => {
  if (status === 'connected') return null;
  const isReconnecting = status === 'Reconnecting...';
  const progress = isReconnecting ? (attempt / maxAttempts) * 100 : 0;

  const displayStatus = {
    'Host is offline': 'The remote device is currently offline',
    'Reconnecting...': 'Re-establishing connection...',
    'Connection lost. Please reconnect.': 'Session ended — please reconnect',
  }[status] || status;

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50 backdrop-blur-md">
      <div className="text-white flex flex-col items-center gap-5 max-w-xs text-center">
        {isReconnecting ? (
          <>
            <div className="relative">
              <div className="w-16 h-16 border-[3px] border-gray-700 rounded-full" />
              <div className="absolute inset-0 w-16 h-16 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
            <div>
              <p className="text-lg font-semibold">Reconnecting</p>
              <p className="text-sm text-gray-400 mt-1">Attempt {attempt} of {maxAttempts}</p>
            </div>
            <div className="w-52 h-1 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500"
                   style={{ width: `${progress}%` }} />
            </div>
          </>
        ) : (
          <>
            <div className="p-4 rounded-full bg-gray-800/80">
              <WifiOff className="w-10 h-10 text-gray-400" />
            </div>
            <div>
              <p className="text-lg font-semibold">{displayStatus}</p>
              <p className="text-sm text-gray-500 mt-1">Check that the host device is running</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const RemoteCanvas = ({ deviceId, passkey, onDisconnect }) => {
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const sessionIdRef = useRef(null);
  const hostSessionIdRef = useRef(null);
  const lastMouseMove = useRef(0);
  const sfuStreamRef = useRef(null);

  const p2pPcRef = useRef(null);
  const p2pDcRef = useRef(null);
  const p2pOpenRef = useRef(false);

  const [status, setStatus] = useState('Connecting...');
  const statusRef = useRef('Connecting...');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [stats, setStats] = useState({ fps: 0, bandwidth: 0 });
  const [reconnectCounter, setReconnectCounter] = useState(0);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [isBuffering, setIsBuffering] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);

  const [isDataChannelOpen, setIsDataChannelOpen] = useState(false);
  const [isVideoConnected, setIsVideoConnected] = useState(false);

  // ─── FIX 2: Track actual input path (P2P direct vs HTTP cloud fallback) ───
  // 'direct' = P2P data channel open
  // 'cloud'  = HTTP fallback active (always true when session is connected)
  // 'none'   = not yet connected
  const [inputPath, setInputPath] = useState('none');

  const frameCount = useRef(0);
  const sessionStartRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectingRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const freezeRecoveryRef = useRef(null);
  const lastFrameAtRef = useRef(Date.now());
  const lastPlaybackFramesRef = useRef(0);
  const p2pNegotiatingRef = useRef(false);
  const p2pGenerationRef = useRef(0);
  const lastDcPongAtRef = useRef(0);
  const mediaStartTimerRef = useRef(null);
  const debugLogs = useDebugLogs();
  const [showDebug, setShowDebug] = useState(DEBUG_MODE);
  const debugPanelRef = useRef(null);

  const inputBatch = useRef([]);
  const inputTimerRef = useRef(null);

  const quality = useConnectionQuality(pcRef);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'webrtc-rebooted') {
          debugLog('SYSTEM', 'Host restarted WebRTC session. Refreshing viewer...');
          window.location.reload();
        }
      } catch (e) {}
    };

    // Assuming you have a WebSocket connection or similar mechanism to receive messages
    // If you are using a WebSocket, you would attach the listener here.
    // For example:
    // socket.addEventListener('message', handleMessage);
    // return () => socket.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (status !== 'connected') return;
    if (!sessionStartRef.current) sessionStartRef.current = Date.now();
    const timer = setInterval(() => {
      setSessionTime(Math.floor((Date.now() - sessionStartRef.current) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [status]);

  // ─── FIX 2: Sync inputPath from isDataChannelOpen ─────────────────────────
  useEffect(() => {
    if (status !== 'connected') {
      setInputPath('none');
      return;
    }
    // Once connected, HTTP fallback is always available → minimum is 'cloud'
    setInputPath(isDataChannelOpen ? 'direct' : 'cloud');
  }, [isDataChannelOpen, status]);

  const resetFrameBaseline = useCallback(() => {
    lastFrameAtRef.current = Date.now();
    lastPlaybackFramesRef.current = videoRef.current?.getVideoPlaybackQuality?.()?.totalVideoFrames || 0;
    frameCount.current = 0;
  }, []);

  const queueInput = useCallback((cmd) => {
    if (statusRef.current !== 'connected' || !sessionIdRef.current) return;
    inputBatch.current.push(cmd);
  }, []);

  const sendViaDc = useCallback((commands) => {
    const dc = p2pDcRef.current;
    const p2pPc = p2pPcRef.current;
    const dcHealthy = dc &&
      dc.readyState === 'open' &&
      dc.bufferedAmount < 256 * 1024 &&
      Date.now() - lastDcPongAtRef.current < 7000 &&
      (!p2pPc || p2pPc.connectionState === 'connected');
    if (dcHealthy) {
      try {
        dc.send(JSON.stringify(commands));
        return true;
      } catch { }
    }
    return false;
  }, []);

  useEffect(() => {
    if (status !== 'connected') return;
    const heartbeat = setInterval(() => {
      const dc = p2pDcRef.current;
      if (!dc || dc.readyState !== 'open') return;

      const age = Date.now() - lastDcPongAtRef.current;
      if (lastDcPongAtRef.current > 0 && age > 7000) {
        debugLog('P2P', `DataChannel heartbeat stale (${age}ms) — falling back to cloud input`);
        p2pOpenRef.current = false;
        setIsDataChannelOpen(false);
        try { dc.close(); } catch {}
        return;
      }

      try {
        dc.send(JSON.stringify([{ type: 'dc_ping', id: `${Date.now()}-${Math.random()}` }]));
      } catch {
        p2pOpenRef.current = false;
        setIsDataChannelOpen(false);
      }
    }, 2000);
    return () => clearInterval(heartbeat);
  }, [status]);

  useEffect(() => {
    if (status !== 'connected') return;
    const check = setInterval(() => {
      const vid = videoRef.current;
      if (vid && vid.readyState >= 2) {
        setIsBuffering(vid.paused || vid.readyState < 3);
      }
    }, 400);
    return () => clearInterval(check);
  }, [status]);

  useEffect(() => {
    if (status !== 'connected') return;

    const FREEZE_THRESHOLD_MS = 3000; // 3 seconds without a new frame = frozen
    let recoveryTriggered = false;

    const freezeWatchdog = setInterval(() => {
      const vid = videoRef.current;
      if (!vid) return;
      if (vid.paused && vid.srcObject) vid.play().catch(() => {});

      const now = Date.now();
      const quality = vid.getVideoPlaybackQuality?.();
      const totalFrames = quality?.totalVideoFrames || 0;

      if (totalFrames > lastPlaybackFramesRef.current) {
        lastPlaybackFramesRef.current = totalFrames;
        lastFrameAtRef.current = now;
        recoveryTriggered = false;
        setIsVideoConnected(true);
        return;
      }

      if (now - lastFrameAtRef.current >= FREEZE_THRESHOLD_MS) {
        const currentStream = vid.srcObject;
        const usingP2PVideo = currentStream && sfuStreamRef.current && currentStream !== sfuStreamRef.current;
        const activePc = usingP2PVideo ? p2pPcRef.current : pcRef.current;
        const connectionAlive = activePc &&
          (activePc.connectionState === 'connected' ||
           activePc.iceConnectionState === 'connected' ||
           activePc.iceConnectionState === 'completed');
        const hasLiveTrack = currentStream?.getVideoTracks?.().some((track) => track.readyState === 'live');

        if (connectionAlive && hasLiveTrack && vid.readyState >= 2) {
          lastFrameAtRef.current = now;
          recoveryTriggered = false;
          setIsVideoConnected(true);
          return;
        }

        debugLog('FREEZE', `No decoded frame for ${FREEZE_THRESHOLD_MS}ms — recovering`);
        setIsVideoConnected(false);
        if (recoveryTriggered) return;
        recoveryTriggered = true;

        const sfuStream = sfuStreamRef.current;
        if (sfuStream && vid.srcObject !== sfuStream) {
          debugLog('FREEZE', 'Direct video frozen — falling back to SFU stream');
          vid.srcObject = sfuStream;
          resetFrameBaseline();
          vid.play().catch(() => {});
          return;
        }

        debugLog('FREEZE', 'SFU video frozen — reconnecting viewer session');
        freezeRecoveryRef.current?.();
      }
    }, 500); // check every 500 ms for fast detection

    return () => clearInterval(freezeWatchdog);
  }, [status, resetFrameBaseline]);
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (status !== 'connected') return;
    const vid = videoRef.current;
    if (!vid) return;

    let active = true;
    if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
      const onFrame = () => {
        frameCount.current++;
        lastFrameAtRef.current = Date.now();
        if (active) vid.requestVideoFrameCallback(onFrame);
      };
      vid.requestVideoFrameCallback(onFrame);
    }

    const fpsInterval = setInterval(() => {
      setStats((prev) => ({ ...prev, fps: frameCount.current }));
      frameCount.current = 0;
    }, 1000);

    let lastBytes = 0;
    let lastPacketsLost = 0;
    let lastPacketsReceived = 0;
    const bwInterval = setInterval(async () => {
      const vid = videoRef.current;
      const usingP2PVideo = vid?.srcObject && sfuStreamRef.current && vid.srcObject !== sfuStreamRef.current;
      const pc = usingP2PVideo ? (p2pPcRef.current || pcRef.current) : pcRef.current;
      if (!pc) return;
      try {
        const st = await pc.getStats();
        let measuredBwKbps = 0;
        let measuredLoss = 0;
        let measuredRtt = 0;
        st.forEach((r) => {
          if (r.type === 'inbound-rtp' && r.kind === 'video') {
            const bps = r.bytesReceived || 0;
            const delta = bps - lastBytes;
            lastBytes = bps;
            measuredBwKbps = Math.round(delta * 8 / 2000);
            const lost = r.packetsLost || 0;
            const recv = r.packetsReceived || 0;
            const deltaLost = Math.max(0, lost - lastPacketsLost);
            const deltaRecv = Math.max(0, recv - lastPacketsReceived);
            lastPacketsLost = lost;
            lastPacketsReceived = recv;
            const intervalPackets = deltaLost + deltaRecv;
            measuredLoss = intervalPackets > 0
              ? (deltaLost / intervalPackets) * 100
              : (lost / Math.max(1, lost + recv)) * 100;
            setStats((prev) => ({ ...prev, bandwidth: measuredBwKbps }));
          }
          if (r.type === 'candidate-pair' && r.state === 'succeeded') {
            measuredRtt = Math.round((r.currentRoundTripTime || 0) * 1000);
          }
        });

        const navConn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        const browserDownlinkKbps = navConn?.downlink ? Math.round(navConn.downlink * 1000) : 0;
        const browserRtt = navConn?.rtt ? Math.round(navConn.rtt) : 0;
        const capacityKbps = Math.max(measuredBwKbps, browserDownlinkKbps);
        const effectiveRtt = measuredRtt || browserRtt;

        if (capacityKbps > 0) {
          let target = { width: 1280, height: 720, fps: 30, bitrate: 2500 };

          if (measuredLoss >= 8 || effectiveRtt >= 350 || capacityKbps < 900) {
            target = { width: 640, height: 360, fps: 15, bitrate: 700 };
          } else if (measuredLoss >= 4 || effectiveRtt >= 240 || capacityKbps < 1800) {
            target = { width: 960, height: 540, fps: 24, bitrate: 1400 };
          } else if (measuredLoss < 2 && effectiveRtt < 170 && capacityKbps >= 8000) {
            target = { width: 1920, height: 1080, fps: 30, bitrate: 6000 };
          } else if (capacityKbps >= 3200) {
            target = { width: 1280, height: 720, fps: 30, bitrate: 2500 };
          } else {
            target = { width: 960, height: 540, fps: 24, bitrate: 1400 };
          }

          const hint = [{
            type: 'quality_hint',
            ...target,
            measuredBw: capacityKbps,
            measuredRtt: effectiveRtt,
            measuredLoss: +measuredLoss.toFixed(1),
          }];
          if (!sendViaDc(hint)) {
            apiFetch('/session/input', {
              method: 'POST',
              body: JSON.stringify({ deviceId, commands: hint }),
            }).catch(() => {});
          }
        }
      } catch {}
    }, 3000);

    return () => {
      active = false;
      clearInterval(fpsInterval);
      clearInterval(bwInterval);
    };
  }, [status, queueInput, sendViaDc, deviceId]);

  const cleanup = useCallback(() => {
    if (inputTimerRef.current) clearInterval(inputTimerRef.current);
    inputTimerRef.current = null;
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    reconnectTimeoutRef.current = null;
    if (mediaStartTimerRef.current) clearTimeout(mediaStartTimerRef.current);
    mediaStartTimerRef.current = null;
    if (p2pDcRef.current) { try { p2pDcRef.current.close(); } catch {} }
    if (p2pPcRef.current) { try { p2pPcRef.current.close(); } catch {} }
    p2pDcRef.current = null;
    p2pPcRef.current = null;
    p2pOpenRef.current = false;
    lastDcPongAtRef.current = 0;
    p2pNegotiatingRef.current = false;
    p2pGenerationRef.current++;
    sfuStreamRef.current = null;

    setIsDataChannelOpen(false);
    setIsVideoConnected(false);
    setInputPath('none');

    if (pcRef.current) {
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.ontrack = null;
      pcRef.current.close();
    }
    pcRef.current = null;
    sessionIdRef.current = null;
    hostSessionIdRef.current = null;
    inputBatch.current = [];
    resetFrameBaseline();

    if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.load();
    }
  }, [resetFrameBaseline]);

  const flushInput = useCallback(() => {
    if (inputBatch.current.length === 0) return;
    if (statusRef.current !== 'connected' || !sessionIdRef.current) {
      inputBatch.current = [];
      return;
    }
    const commands = inputBatch.current.splice(0, inputBatch.current.length);
    if (sendViaDc(commands)) return;
    apiFetch('/session/input', {
      method: 'POST',
      body: JSON.stringify({ deviceId, commands }),
    }).catch(() => {});
  }, [deviceId, sendViaDc]);

  const sendImmediate = useCallback((cmd) => {
    if (statusRef.current !== 'connected' || !sessionIdRef.current) return;
    inputBatch.current.push(cmd);
    const commands = inputBatch.current.splice(0, inputBatch.current.length);
    if (sendViaDc(commands)) return;
    apiFetch('/session/input', {
      method: 'POST',
      body: JSON.stringify({ deviceId, commands }),
    }).catch(() => {});
  }, [deviceId, sendViaDc]);

  useEffect(() => {
    let cancelled = false;

    function triggerReconnect() {
      if (reconnectingRef.current || cancelled) return;
      reconnectingRef.current = true;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);

      const browserOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
      const attempt = browserOffline ? reconnectAttemptRef.current : reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      setReconnectAttempt(attempt);

      cleanup();

      if (browserOffline) {
        setStatus('Waiting for internet...');
        debugLog('RECONNECT', 'Browser is offline — pausing reconnect counter');
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectingRef.current = false;
          if (!cancelled) setReconnectCounter((c) => c + 1);
        }, 5000);
        return;
      }

      if (attempt <= MAX_RECONNECT_ATTEMPTS) {
        setStatus('Reconnecting...');
        const delay = RECONNECT_DELAYS[Math.min(attempt - 1, RECONNECT_DELAYS.length - 1)];
        debugLog('RECONNECT', `Backoff triggered: Waiting ${delay}ms before Attempt ${attempt}...`);

        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectingRef.current = false;
          if (!cancelled) setReconnectCounter((c) => c + 1);
        }, delay);
      } else {
        setStatus('Connection lost. Waiting for recovery...');
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectingRef.current = false;
          if (!cancelled) setReconnectCounter((c) => c + 1);
        }, 15000);
      }
    }
    freezeRecoveryRef.current = triggerReconnect;

    const handleBrowserOnline = () => {
      const pc = pcRef.current;
      const pcUnhealthy = !pc || ['failed', 'disconnected', 'closed'].includes(pc.connectionState);
      const mediaStale = Date.now() - lastFrameAtRef.current > 5000;
      if (statusRef.current === 'connected' && !pcUnhealthy && !mediaStale) {
        debugLog('NETWORK', 'Browser online event ignored — active session is healthy');
        return;
      }
      reconnectAttemptRef.current = 0;
      setReconnectAttempt(0);
      reconnectingRef.current = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
      setReconnectCounter((c) => c + 1);
    };
    const handleBrowserOffline = () => {
      debugLog('NETWORK', 'Browser offline — viewer will wait for network recovery');
    };
    window.addEventListener('online', handleBrowserOnline);
    window.addEventListener('offline', handleBrowserOffline);

    async function connect() {
      try {
        debugLog('CONNECT', `Starting connection to device: ${deviceId.trim()}`);
        setStatus('Verifying host...');
        let st = null;
        for (let attempt = 1; attempt <= 3; attempt++) {
          st = await apiFetch(`/session/status/${encodeURIComponent(deviceId.trim())}`);
          if (cancelled) return;
          if (st.success && st.online) break;
          await new Promise(r => setTimeout(r, 1500));
        }

        if (!st?.success || !st?.online) {
          throw new Error('Host is currently offline or rebooting');
        }
        hostSessionIdRef.current = st.sessionId || null;

        setStatus('Establishing secure connection...');
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
          bundlePolicy: 'max-bundle',
          rtcpMuxPolicy: 'require',
        });
        pcRef.current = pc;

        const transceiver = pc.addTransceiver('video', { direction: 'recvonly' });
        const capabilities = RTCRtpReceiver.getCapabilities?.('video');
        if (capabilities) {
          const h264Codecs = capabilities.codecs.filter((c) => c.mimeType.toLowerCase() === 'video/h264');
          if (h264Codecs.length > 0) transceiver.setCodecPreferences(h264Codecs);
        }

        pc.oniceconnectionstatechange = () => {
          const state = pc.iceConnectionState;
          debugLog('ICE', `Connection state → ${state}`);
          if (state === 'disconnected' || state === 'failed') {
            setTimeout(() => {
              if ((pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') && !cancelled) {
                debugLog('ICE', 'ICE Tunnel collapsed. Forcing reconnect...');
                triggerReconnect();
              }
            }, 3000);
          }
        };

        pc.ontrack = (ev) => {
          ev.track.onunmute = () => {
            setIsVideoConnected(true);
            debugLog('TRACK', 'Track UNMUTED — frames arriving');
          };
          ev.track.onmute = () => {
            setIsVideoConnected(false);
            debugLog('TRACK', 'Track MUTED (Stream paused)');
          };
          ev.track.onended = () => {
            setIsVideoConnected(false);
            debugLog('TRACK', 'Track ENDED — Host restarted WebRTC session');
            if (!cancelled) triggerReconnect();
          };

          if (cancelled) return;
          const vid = videoRef.current;
          if (!vid) return;
          let videoReady = false;
          const markVideoReady = () => {
            if (cancelled || videoReady) return;
            videoReady = true;
            if (mediaStartTimerRef.current) clearTimeout(mediaStartTimerRef.current);
            mediaStartTimerRef.current = null;
            resetFrameBaseline();
            setIsVideoConnected(true);
            setStatus('connected');
            setReconnectAttempt(0);
            reconnectAttemptRef.current = 0;
            reconnectingRef.current = false;
            debugLog('TRACK', 'First decoded frame received');
          };

          let stream;
          if (ev.streams && ev.streams.length > 0) {
            stream = ev.streams[0];
          } else {
            stream = new MediaStream([ev.track]);
          }

          sfuStreamRef.current = stream;

          if (vid.srcObject !== stream) {
              resetFrameBaseline();
              vid.srcObject = stream;
              vid.load();
          }

          const receiver = pc.getReceivers().find(r => r.track?.kind === 'video');
          if (receiver && receiver.jitterBufferTarget !== undefined) receiver.jitterBufferTarget = 0;

          vid.play().catch(() => {});
          if (mediaStartTimerRef.current) clearTimeout(mediaStartTimerRef.current);
          mediaStartTimerRef.current = setTimeout(() => {
            if (cancelled || videoReady) return;
            debugLog('TRACK', 'No decoded frame after subscribe — reconnecting');
            triggerReconnect();
          }, 12000);
          if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
            vid.requestVideoFrameCallback(() => markVideoReady());
          } else {
            vid.addEventListener('playing', markVideoReady, { once: true });
          }
        };

        let subscribed = false;
        pc.onconnectionstatechange = async () => {
          const state = pc.connectionState;
          if (state === 'connected' && !subscribed && !cancelled) {
            subscribed = true;
            setStatus('Connecting to remote display...');
            try {
              const MAX_SUB_RETRIES = 20;
              let sub = null;

              for (let attempt = 1; attempt <= MAX_SUB_RETRIES && !cancelled; attempt++) {
                sub = await apiFetch('/session/subscribe-track', {
                  method: 'POST',
                  body: JSON.stringify({ deviceId: deviceId.trim(), passkey: passkey.trim(), viewerSessionId: sessionIdRef.current }),
                });

                if (sub.success) break;
                const isRetryable = sub.retryable || (sub.error && sub.error.includes('Track not found')) || (sub.error && sub.error.includes('not yet published'));

                if (isRetryable && attempt < MAX_SUB_RETRIES) {
                  await new Promise((r) => setTimeout(r, 300));
                  continue;
                }
                throw new Error('Unable to subscribe to host track');
              }

              if (!sub || !sub.success || cancelled) return;

              if (sub.requiresRenegotiation && sub.cfOfferSDP) {
                await pc.setRemoteDescription({ type: 'offer', sdp: sub.cfOfferSDP });
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                const nego = await apiFetch('/session/negotiate', {
                  method: 'POST',
                  body: JSON.stringify({ sessionId: sessionIdRef.current, answerSDP: pc.localDescription.sdp }),
                });
                if (!nego.success) throw new Error('Renegotiation failed');
              }
              setStatus((prev) => prev === 'connected' ? prev : 'Waiting for remote video...');
            } catch (err) {
              debugLog('SUB', `EXCEPTION: ${err.message}`);
              if (!cancelled) triggerReconnect();
            }
          } else if (state === 'failed') {
            if (!cancelled) triggerReconnect();
          }
        };

        setStatus('Establishing connection...');
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const mid = pc.getTransceivers()[0]?.mid || '0';

        const sess = await apiFetch('/session/new', {
          method: 'POST',
          body: JSON.stringify({ deviceId: deviceId.trim(), passkey: passkey.trim(), role: 'viewer', offerSDP: pc.localDescription.sdp, mid }),
        });

        if (cancelled) return;
        if (!sess.success) throw new Error('Failed to create session with Cloudflare');

        sessionIdRef.current = sess.sessionId;

        if (!sess.answerSDP || typeof sess.answerSDP !== 'string' || !sess.answerSDP.startsWith('v=')) {
           throw new Error('Invalid answer SDP from Cloudflare');
        }

        await pc.setRemoteDescription({ type: 'answer', sdp: sess.answerSDP });
        setStatus('Starting remote display...');
        if (!inputTimerRef.current) {
          inputTimerRef.current = setInterval(flushInput, INPUT_FLUSH_MS);
        }

        (async function negotiateP2PDataChannel() {
          if (p2pNegotiatingRef.current || cancelled) return;
          p2pNegotiatingRef.current = true;
          const generation = ++p2pGenerationRef.current;
          let p2pPc = null;
          let dc = null;
          let established = false;
          const isCurrentP2P = () => !cancelled && generation === p2pGenerationRef.current;
          const closeLocalP2P = () => {
            try { dc?.close(); } catch {}
            try { p2pPc?.close(); } catch {}
            if (p2pDcRef.current === dc) p2pDcRef.current = null;
            if (p2pPcRef.current === p2pPc) p2pPcRef.current = null;
          };
          try {
            if (p2pDcRef.current) { try { p2pDcRef.current.close(); } catch {} }
            if (p2pPcRef.current) { try { p2pPcRef.current.close(); } catch {} }
            p2pDcRef.current = null;
            p2pPcRef.current = null;

            let iceServers = [{ urls: 'stun:stun.l.google.com:19302' }];
            try {
              const turnRes = await fetch(`${API_BASE.replace('/cf-calls', '/rdp/signal')}/turn-credentials?deviceId=${encodeURIComponent(deviceId.trim())}&passkey=${encodeURIComponent(passkey.trim())}`, { cache: 'no-store' });
              const turnData = await turnRes.json();
              if (turnData.success && turnData.iceServers) iceServers = [...turnData.iceServers, ...iceServers];
            } catch (e) {}

            p2pPc = new RTCPeerConnection({ iceServers, bundlePolicy: 'max-bundle' });
            p2pPcRef.current = p2pPc;

            dc = p2pPc.createDataChannel('input', { ordered: true });
            p2pDcRef.current = dc;

            dc.onopen = () => {
                if (!isCurrentP2P()) return;
                p2pOpenRef.current = true;
                lastDcPongAtRef.current = Date.now();
                setIsDataChannelOpen(true);
            };

            dc.onclose = () => {
                if (!isCurrentP2P()) return;
                p2pOpenRef.current = false;
                lastDcPongAtRef.current = 0;
                setIsDataChannelOpen(false);

                if (!cancelled) {
                    debugLog('P2P', 'DataChannel closed — scheduling re-negotiation in 3s');
                    setTimeout(() => {
                        if (isCurrentP2P() && p2pOpenRef.current === false) {
                            debugLog('P2P', 'Re-starting P2P negotiation after drop');
                            negotiateP2PDataChannel();
                        }
                    }, 3000);
                }
            };

            dc.onmessage = (ev) => {
                if (!isCurrentP2P()) return;
                try {
                    const msg = JSON.parse(ev.data);
                    if (msg?.type === 'dc_pong') {
                        lastDcPongAtRef.current = Date.now();
                    }
                } catch {}
            };

            const localCandidates = [];
            let gatheringDone = false;
            p2pPc.onicecandidate = (ev) => {
              if (ev.candidate) localCandidates.push(ev.candidate.toJSON());
              else gatheringDone = true;
            };

            const p2pTransceiver = p2pPc.addTransceiver('video', { direction: 'recvonly' });
            const caps = RTCRtpReceiver.getCapabilities?.('video');
            if (caps) {
                const h264 = caps.codecs.filter(c => c.mimeType.toLowerCase() === 'video/h264');
                if (h264.length > 0) p2pTransceiver.setCodecPreferences(h264);
            }

            p2pPc.ontrack = (ev) => {
                if (!isCurrentP2P() || ev.track.kind !== 'video') return;
                const vid = videoRef.current;
                if (!vid) return;
                const p2pStream = ev.streams?.[0] || new MediaStream([ev.track]);

                const attachP2P = () => {
                    if (!isCurrentP2P()) return;
                    debugLog('P2P', 'Attaching Direct P2P Video Stream!');
                    resetFrameBaseline();
                    vid.srcObject = p2pStream;
                    vid.play().catch(() => {});
                    setIsVideoConnected(true);
                };

                const fallbackToSFU = () => {
                    if (!isCurrentP2P()) return;
                    setIsVideoConnected(false);
                    debugLog('P2P', 'P2P video lost — falling back to CF SFU stream');
                    const sfuStream = sfuStreamRef.current;
                    if (sfuStream && vid.srcObject !== sfuStream) {
                        resetFrameBaseline();
                        vid.srcObject = sfuStream;
                        vid.play().catch(() => {});
                        setIsVideoConnected(true);
                    }
                };

                if (!ev.track.muted) {
                    attachP2P();
                } else {
                    ev.track.addEventListener('unmute', attachP2P, { once: true });
                    const unmuteFallbackTimer = setTimeout(() => {
                        if (!isCurrentP2P()) return;
                        if (vid.srcObject !== p2pStream && !ev.track.muted) {
                            debugLog('P2P', 'onunmute fallback timer triggered');
                            attachP2P();
                        }
                    }, 8000);
                    ev.track.addEventListener('unmute', () => clearTimeout(unmuteFallbackTimer), { once: true });
                }

                ev.track.addEventListener('mute', fallbackToSFU);
                ev.track.addEventListener('ended', fallbackToSFU);
            };

            const offer = await p2pPc.createOffer();
            await p2pPc.setLocalDescription(offer);

            if (!gatheringDone) {
              await new Promise((resolve) => {
                const check = setInterval(() => {
                  if (gatheringDone || p2pPc.iceGatheringState === 'complete') { clearInterval(check); resolve(); }
                }, 100);
                setTimeout(() => { clearInterval(check); resolve(); }, 5000);
              });
            }

            if (!isCurrentP2P()) return;

            await apiFetch('/session/p2p-signal', {
              method: 'POST',
              body: JSON.stringify({ deviceId: deviceId.trim(), passkey: passkey.trim(), type: 'offer', role: 'viewer', data: { sdp: p2pPc.localDescription.sdp, type: 'offer' } }),
            });

            for (const c of localCandidates) {
              await apiFetch('/session/p2p-signal', {
                method: 'POST',
                body: JSON.stringify({ deviceId: deviceId.trim(), passkey: passkey.trim(), type: 'ice', role: 'viewer', data: c }),
              });
            }

            let answer = null;
            for (let i = 0; i < 30 && !cancelled; i++) {
              const res = await apiFetch(`/session/p2p-signal/${encodeURIComponent(deviceId.trim())}?type=answer&role=host`);
              if (res.success && res.data) { answer = res.data; break; }
              await new Promise(r => setTimeout(r, 1000));
            }

            if (!answer || !isCurrentP2P()) return;
            await p2pPc.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
            if (!isCurrentP2P()) return;
            established = true;

            const seenCandidates = new Set();
            for (let i = 0; i < 20 && !cancelled; i++) {
                const res = await apiFetch(
                    `/session/p2p-signal/${encodeURIComponent(deviceId.trim())}?type=ice&role=host`
                );
                if (res.success && res.data && res.data.length > 0) {
                    for (const c of res.data) {
                        const key = c.candidate || JSON.stringify(c);
                        if (seenCandidates.has(key)) continue;
                        seenCandidates.add(key);
                        try { await p2pPc.addIceCandidate(new RTCIceCandidate(c)); } catch {}
                    }
                }
                await new Promise(r => setTimeout(r, 500));
            }
          } catch (err) {
            debugLog('P2P', `Negotiation failed: ${err.message}`);
          } finally {
            if (generation === p2pGenerationRef.current) {
              p2pNegotiatingRef.current = false;
            }
            if (!established) closeLocalP2P();
          }
        })();

      } catch (err) {
        debugLog('ERROR', `Connection exception: ${err.message}`);
        if (!cancelled) triggerReconnect();
      }
    }

    connect();

    const watchdogTimer = setInterval(async () => {
      if (cancelled || reconnectingRef.current) return;
      try {
        const st = await apiFetch(`/session/status/${encodeURIComponent(deviceId.trim())}`);
        if (st.success && st.online && st.sessionId && hostSessionIdRef.current) {
          if (st.sessionId !== hostSessionIdRef.current) {
            debugLog('WATCHDOG', `Host shifted to new session: ${st.sessionId}. Reconnecting!`);
            // STABILITY FIX 4.2: Reset reconnect counter so we actually retry
            hostSessionIdRef.current = st.sessionId;
            reconnectAttemptRef.current = 0;
            setReconnectAttempt(0);
            triggerReconnect();
          }
        } else if (st.success && st.online && st.sessionId && !sessionIdRef.current && !reconnectingRef.current) {
          // STABILITY FIX 4.2: Host came back online after we gave up — auto-reconnect
          debugLog('WATCHDOG', 'Host is back online! Auto-reconnecting...');
          reconnectAttemptRef.current = 0;
          setReconnectAttempt(0);
          setReconnectCounter((c) => c + 1);
        }
        // REACT-BUG-1 FIX: Do NOT call triggerReconnect when host is simply offline.
        // Doing so burns through MAX_RECONNECT_ATTEMPTS (5 × every 5s = 25s) before the
        // host even has a chance to come back. The ICE failure handlers already trigger
        // reconnection. The watchdog should only react to SESSION ID changes or
        // host coming back online (handled above).
        // The old code: else if (!st.online) { triggerReconnect(); } was the bug.
      } catch (e) {}
    }, 5000);

    return () => {
      cancelled = true;
      if (freezeRecoveryRef.current === triggerReconnect) freezeRecoveryRef.current = null;
      window.removeEventListener('online', handleBrowserOnline);
      window.removeEventListener('offline', handleBrowserOffline);
      clearInterval(watchdogTimer);
      cleanup();
    };
  }, [deviceId, passkey, cleanup, flushInput, reconnectCounter, resetFrameBaseline]);

  const getRelativePos = (e) => {
    const vid = videoRef.current;
    if (!vid) return { x: 0, y: 0 };
    const rect = vid.getBoundingClientRect();
    const vw = vid.videoWidth || rect.width;
    const vh = vid.videoHeight || rect.height;
    const videoAspect = vw / vh;
    const elemAspect = rect.width / rect.height;
    let renderW, renderH, offsetX, offsetY;
    if (videoAspect > elemAspect) {
      renderW = rect.width;
      renderH = rect.width / videoAspect;
      offsetX = 0;
      offsetY = (rect.height - renderH) / 2;
    } else {
      renderH = rect.height;
      renderW = rect.height * videoAspect;
      offsetX = (rect.width - renderW) / 2;
      offsetY = 0;
    }
    const relX = (e.clientX - rect.left - offsetX) / renderW;
    const relY = (e.clientY - rect.top - offsetY) / renderH;
    return {
      x: Math.max(0, Math.min(1, relX)),
      y: Math.max(0, Math.min(1, relY)),
    };
  };

  const handleMouseMove = (e) => {
    const now = Date.now();
    if (now - lastMouseMove.current < 16) return;
    lastMouseMove.current = now;
    queueInput({ type: 'mousemove', ...getRelativePos(e) });
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.currentTarget.closest('[tabindex]')?.focus();
    sendImmediate({ type: 'mousedown', button: e.button, ...getRelativePos(e) });
  };

  const handleMouseUp = (e) => {
    e.preventDefault();
    sendImmediate({ type: 'mouseup', button: e.button, ...getRelativePos(e) });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    sendImmediate({ type: 'mousewheel', deltaY: Math.sign(e.deltaY) * 120, ...getRelativePos(e) });
  };

  const handleKeyDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    sendImmediate({ type: 'keydown', key: e.key, code: e.code, keyCode: e.keyCode, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, metaKey: e.metaKey });
  };

  const handleKeyUp = (e) => {
    e.preventDefault(); e.stopPropagation();
    sendImmediate({ type: 'keyup', key: e.key, code: e.code, keyCode: e.keyCode, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, metaKey: e.metaKey });
  };

  const toggleFullscreen = () => {
    const container = document.querySelector('.remote-container');
    if (!document.fullscreenElement) {
      container.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

 const handleManualDisconnect = async () => {
    debugLog('SYSTEM', 'Viewer session closed locally.');
    cleanup();
    if (onDisconnect) onDisconnect();
  };

  const getLatencyColor = (ms) => {
    if (ms === 0) return 'text-gray-500';
    if (ms < 50) return 'text-green-400';
    if (ms < 100) return 'text-yellow-400';
    return 'text-red-400';
  };

  // ─── FIX 2: Input path badge helpers ──────────────────────────────────────
  const inputPathLabel = inputPath === 'direct' ? 'Direct' : inputPath === 'cloud' ? 'Cloud' : '—';
  const inputPathColor = inputPath === 'direct'
    ? 'bg-emerald-400'
    : inputPath === 'cloud'
      ? 'bg-blue-400'
      : 'bg-red-400';
  const inputPathTextColor = inputPath === 'direct'
    ? 'text-emerald-300'
    : inputPath === 'cloud'
      ? 'text-blue-300'
      : 'text-gray-500';
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <div className="remote-container fixed inset-0 bg-gray-950 flex flex-col" id="remote-desktop-viewer">
      <div className="bg-gray-900/95 border-b border-gray-800/80 px-4 py-2 flex items-center justify-between z-20 shadow-lg backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
              <Monitor className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-white font-semibold text-sm tracking-tight">CheckScript Remote</span>
          </div>

          <div className="h-4 w-px bg-gray-700" />

          <div className="flex items-center gap-2 px-3 py-1 bg-gray-800/60 rounded-lg border border-gray-800">
            {status === 'connected' ? (
              <>
                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                <span className="text-emerald-400 text-xs font-medium">Live</span>
              </>
            ) : status === 'Reconnecting...' ? (
              <>
                <RefreshCw className="w-3 h-3 text-amber-400 animate-spin" />
                <span className="text-amber-400 text-xs">Reconnecting</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 bg-red-400 rounded-full" />
                <span className="text-gray-400 text-xs">{status}</span>
              </>
            )}
          </div>

          {/* Video indicator — now driven by freeze watchdog too */}
          <div className="flex items-center gap-2 px-2.5 py-1 bg-gray-800/40 rounded-lg border border-gray-800">
            <div className={`w-2 h-2 rounded-full ${isVideoConnected ? 'bg-emerald-400' : 'bg-red-400'}`} />
            <span className="text-[11px] text-gray-300 font-medium">Video</span>
          </div>

          {/*
            ─── FIX 2: Replaced the binary "DataChannel" indicator with an
            "Input" indicator that shows the actual active path.
            • "Direct"  (green) = P2P WebRTC data channel is open
            • "Cloud"   (blue)  = HTTP fallback is active (always true when connected)
            • "—"       (red)   = not connected yet
            This prevents the indicator from being permanently red when P2P
            NAT traversal fails but HTTP input still works correctly.
          ───────────────────────────────────────────────────────────────── */}
          <div className="flex items-center gap-2 px-2.5 py-1 bg-gray-800/40 rounded-lg border border-gray-800" title={inputPath === 'direct' ? 'P2P WebRTC data channel (lowest latency)' : inputPath === 'cloud' ? 'HTTP cloud relay (working normally)' : 'Not connected'}>
            <div className={`w-2 h-2 rounded-full ${inputPathColor}`} />
            <span className={`text-[11px] font-medium ${inputPathTextColor}`}>
              Input: {inputPathLabel}
            </span>
          </div>

          {status === 'connected' && (
            <div className="flex items-center gap-2 px-2.5 py-1 bg-gray-800/40 rounded-lg border border-gray-800">
              <Shield className="w-3 h-3 text-orange-400/60" />
              <span className="text-[11px] text-orange-400/60 font-medium">Cloudflare</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {status === 'connected' && (
            <div className="flex items-center gap-3 text-xs text-gray-400 bg-gray-800/40 px-3 py-1.5 rounded-lg border border-gray-800">
              <div className="flex items-center gap-1" title="Frames per second">
                <Activity className="w-3 h-3" />
                <span className="font-mono text-[11px]">{stats.fps} fps</span>
              </div>
              <div className="w-px h-3 bg-gray-700" />
              <div className="flex items-center gap-1" title="Bandwidth">
                <Zap className="w-3 h-3" />
                <span className="font-mono text-[11px]">{stats.bandwidth} kbps</span>
              </div>
              <div className="w-px h-3 bg-gray-700" />
              <div className={`flex items-center gap-1 ${getLatencyColor(quality.latency)}`} title="Latency">
                <Globe className="w-3 h-3" />
                <span className="font-mono text-[11px]">{quality.latency}ms</span>
              </div>
              <div className="w-px h-3 bg-gray-700" />
              <div className="flex items-center gap-1 text-gray-500" title="Session time">
                <Clock className="w-3 h-3" />
                <span className="font-mono text-[11px]">{formatDuration(sessionTime)}</span>
              </div>
            </div>
          )}

          <button
            onClick={toggleFullscreen}
            className="p-1.5 hover:bg-gray-800 rounded-lg transition-all text-gray-500 hover:text-white active:scale-95"
            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          <button
            onClick={async () => {
              setStatus('Restarting Host...');
              await apiFetch('/session/input', {
                method: 'POST',
                body: JSON.stringify({ deviceId: deviceId.trim(), commands: [{ type: 'sys_rebirth' }] }),
              });
              // Your existing watchdog will automatically reconnect when the host wakes up in 3 seconds
            }}
            className="bg-orange-500/90 hover:bg-orange-500 px-3.5 py-1.5 rounded-lg text-white text-xs font-medium transition-all active:scale-95"
            title="Force the host to restart the WebRTC session"
          >
            Restart Host
          </button>

          <button
            onClick={handleManualDisconnect}
            className="bg-red-600/90 hover:bg-red-600 px-3.5 py-1.5 rounded-lg text-white text-xs font-medium transition-all active:scale-95">
            End Session
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center overflow-hidden bg-gray-900 relative">
        <ReconnectOverlay status={status} attempt={reconnectAttempt} maxAttempts={MAX_RECONNECT_ATTEMPTS} />
        <BufferingOverlay isBuffering={isBuffering && status === 'connected'} />

        <div
          className="relative w-full h-full focus:outline-none focus:ring-2 focus:ring-blue-500/50 overflow-hidden shadow-2xl bg-black"
          tabIndex="0"
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
             onLoadedMetadata={(e) => {
                const vid = e.target;
                if ('latencyHint' in HTMLVideoElement.prototype) vid.latencyHint = 0;
                vid.play().catch(() => {});
            }}
            onCanPlay={(e) => e.target.play().catch(() => {})}
            onMouseMove={handleMouseMove}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onWheel={handleWheel}
            onContextMenu={(e) => e.preventDefault()}
            className="w-full h-full block bg-black"
            style={{ objectFit: 'contain', cursor: 'crosshair' }}
          />
        </div>

        {status === 'connected' && (
          <div className="absolute bottom-4 right-4 flex items-center gap-1.5 bg-black/30 px-2.5 py-1 rounded-md backdrop-blur-sm select-none pointer-events-none z-10">
            <Zap className="w-3 h-3 text-orange-400/50" />
            <span className="text-[10px] text-white/30 font-medium tracking-wider uppercase">Powered by Cloudflare</span>
          </div>
        )}
      </div>

      <div className="bg-gray-900/95 border-t border-gray-800/80 px-4 py-1.5 text-[11px] text-gray-600 flex items-center justify-between backdrop-blur-sm">
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-1.5">
            <MousePointer className="w-3 h-3" />
            <span>Click &middot; Scroll &middot; Right-click</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Keyboard className="w-3 h-3" />
            <span>Click video to enable keyboard</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-orange-400/40">
          <Shield className="w-3 h-3" />
          <span>Powered by Cloudflare</span>
        </div>
      </div>

      {showDebug && (
        <div
          style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, maxHeight: '40vh', overflowY: 'auto',
            background: 'rgba(0,0,0,0.92)', color: '#0f0', fontFamily: 'monospace', fontSize: '11px', lineHeight: '1.4',
            zIndex: 9999, padding: '4px 8px', borderTop: '1px solid #333',
          }}
          ref={debugPanelRef}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, position: 'sticky', top: 0, background: 'rgba(0,0,0,0.95)', padding: '2px 0' }}>
            <span style={{ color: '#ff0', fontWeight: 'bold' }}>DEBUG LOG ({debugLogs.length})</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { _debugLogs.length = 0; _debugSubscribers.forEach(fn => fn([])); }} style={{ color: '#f66', background: 'none', border: '1px solid #f66', borderRadius: 3, padding: '1px 6px', cursor: 'pointer', fontSize: 10 }}>Clear</button>
              <button onClick={() => setShowDebug(false)} style={{ color: '#aaa', background: 'none', border: '1px solid #aaa', borderRadius: 3, padding: '1px 6px', cursor: 'pointer', fontSize: 10 }}>Hide</button>
            </div>
          </div>
          {debugLogs.map((l) => (
            <div key={l.id} style={{ borderBottom: '1px solid #222', padding: '1px 0' }}>
              <span style={{ color: '#888' }}>{l.ts}</span>{' '}
              <span style={{ color: l.tag === 'ERROR' ? '#f44' : l.tag === 'HTTP' ? '#4af' : '#0f0', fontWeight: 'bold' }}>[{l.tag}]</span>{' '}
              <span style={{ color: '#ddd' }}>{l.msg}</span>
              {l.data && <span style={{ color: '#a8a' }}> {l.data}</span>}
            </div>
          ))}
          <div ref={(el) => { if (el) el.scrollIntoView({ behavior: 'smooth' }); }} />
        </div>
      )}
      {!showDebug && DEBUG_MODE && (
        <button
          onClick={() => setShowDebug(true)}
          style={{
            position: 'fixed', bottom: 8, right: 8, zIndex: 9999, background: '#222', color: '#0f0', border: '1px solid #0f0',
            borderRadius: 6, padding: '4px 10px', fontFamily: 'monospace', fontSize: 11, cursor: 'pointer', opacity: 0.7,
          }}
        >
          Show Debug
        </button>
      )}
    </div>
  );
};

const FEATURES = [
  { icon: Eye, title: 'Unrestricted Screen Capture', desc: 'Bypasses standard screen-sharing blocks.' },
  { icon: Shield, title: 'Enterprise-Grade Security', desc: 'End-to-end encrypted sessions with per-device codes.' },
  { icon: Globe, title: 'Global Edge Network', desc: 'Powered by Cloudflare\'s 300+ data centers worldwide.' },
  { icon: Cpu, title: 'Hardware-Accelerated Streaming', desc: 'Native H.264 encoding on the host with GPU-decoded playback.' },
  { icon: Layers, title: 'Cross-Platform Viewer', desc: 'View any remote Windows desktop from modern browsers.' },
  { icon: Lock, title: 'Zero-Install Access', desc: 'No plugins, extensions, or software required.' },
];

const RemoteSupport = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [creds, setCreds] = useState({ deviceId: '', passkey: '' });
  const [error, setError] = useState('');
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % FEATURES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const handleConnect = () => {
    if (!creds.deviceId.trim() || !creds.passkey.trim()) {
      setError('Please enter both Device ID and Access Code');
      return;
    }
    setError('');
    setIsConnected(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleConnect();
  };

  if (isConnected) return <RemoteCanvas deviceId={creds.deviceId.trim()} passkey={creds.passkey.trim()} onDisconnect={() => setIsConnected(false)} />;

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col lg:flex-row">
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-gray-900 to-indigo-950" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-600/10 rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg shadow-blue-900/40">
                <Monitor className="w-6 h-6 text-white" />
              </div>
              <span className="text-white text-xl font-bold tracking-tight">CheckScript Remote</span>
            </div>
            <p className="text-gray-400 text-sm ml-[52px]">Professional Remote Desktop Solution</p>
          </div>
          <div className="space-y-10">
            <div>
              <h1 className="text-4xl xl:text-5xl font-extrabold text-white leading-tight">Access any desktop,<br /><span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">from anywhere.</span></h1>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                const isActive = i === activeFeature;
                return (
                  <div key={i} className={`group p-4 rounded-xl border transition-all duration-500 ${isActive ? 'bg-white/[0.06] border-blue-500/30 shadow-lg shadow-blue-900/20' : 'bg-white/[0.02] border-white/[0.06]'}`} onMouseEnter={() => setActiveFeature(i)}>
                    <div className="flex items-start gap-3">
                      <div className={`p-1.5 rounded-lg transition-colors duration-500 ${isActive ? 'bg-blue-500/20 text-blue-400' : 'bg-white/5 text-gray-500 group-hover:text-gray-400'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm font-semibold transition-colors duration-500 ${isActive ? 'text-white' : 'text-gray-300'}`}>{f.title}</h3>
                        <p className={`text-xs mt-1 leading-relaxed transition-all duration-500 ${isActive ? 'text-gray-400 max-h-20 opacity-100' : 'text-gray-600 max-h-0 opacity-0 overflow-hidden'}`}>{f.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-gray-500 text-xs">
              <Shield className="w-3.5 h-3.5 text-orange-400/50" />
              <span>Secured by <span className="text-orange-400/70 font-medium">Cloudflare</span></span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 lg:px-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-950 lg:bg-gray-950" />
        <div className="relative z-10 w-full max-w-sm">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white">Connect to a device</h2>
            <p className="text-gray-500 text-sm mt-1.5">Enter your credentials to start a remote session.</p>
          </div>
          {error && (
            <div className="mb-6 bg-red-950/50 border border-red-800/40 text-red-300 px-4 py-3 rounded-xl text-sm flex items-start gap-2.5">
              <WifiOff className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
              <span>{error}</span>
            </div>
          )}
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Device ID</label>
              <div className="relative">
                <Monitor className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input type="text" className="block w-full bg-gray-900 border border-gray-800 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-600 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all text-sm" value={creds.deviceId} onChange={(e) => setCreds({ ...creds, deviceId: e.target.value })} onKeyPress={handleKeyPress} placeholder="e.g. A1B2C3D4E5F6" autoComplete="off" spellCheck="false" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Access Code</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input type="password" className="block w-full bg-gray-900 border border-gray-800 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-600 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all text-sm" value={creds.passkey} onChange={(e) => setCreds({ ...creds, passkey: e.target.value })} onKeyPress={handleKeyPress} placeholder="Enter access code" autoComplete="off" />
              </div>
            </div>
            <button onClick={handleConnect} className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-950 focus:ring-blue-500 transition-all active:scale-[0.98] shadow-lg shadow-blue-900/25">
              Start Session <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoteSupport;
